// Les libellés de l'éditeur de profil, en français et en anglais.
//
// Même forme que 'FR_SCORE_LABELS' : l'éditeur est commun aux deux apps, mais
// chacune a son propre dispositif de traduction (i18n multilingue dans
// l'extension, dictionnaires bilingues dans la console). Plutôt que d'imposer
// les clés de l'une à l'autre, le composant reçoit un objet de libellés typé :
// un libellé oublié se voit à la compilation, pas à l'écran.

import type { CompletenessKey } from '@ai-job-fit/engine/schema'
import type { UiLang } from '@ai-job-fit/engine/langues'

export interface ProfileEditorLabels {
  completeness: string
  /** '{list}' : les critères non remplis, séparés par des virgules. */
  completenessTodo: string
  /** Les critères de complétude, un par clé de 'profileCompleteness'. Ils
   *  manquaient dans l'extension, qui affichait donc la clé brute
   *  ('options.editor.weak.metrics') sous la barre de complétude. */
  weak: Record<CompletenessKey, string>
  sectionIdentity: string
  sectionWork: string
  sectionSkills: string
  sectionLanguages: string
  sectionEducation: string
  sectionProjects: string
  addWork: string
  addLanguage: string
  addEducation: string
  addProject: string
  emptyWork: string
  emptyLanguages: string
  emptyEducation: string
  itemWork: string
  itemEducation: string
  itemProject: string
  moveUp: string
  moveDown: string
  delete: string
  fieldName: string
  fieldLabel: string
  fieldEmail: string
  fieldPhone: string
  fieldLocation: string
  fieldSummary: string
  fieldPosition: string
  fieldCompany: string
  fieldStart: string
  fieldEnd: string
  fieldCurrent: string
  fieldToday: string
  fieldDegree: string
  fieldArea: string
  fieldInstitution: string
  fieldProjectName: string
  fieldDescription: string
  fieldLanguage: string
  fieldFluency: string
  fieldLinks: string
  fieldProjectUrl: string
  addLink: string
  phLink: string
  /** Sous une adresse que le CV n'affichera pas (pas une URL web). */
  linkInvalid: string
  phName: string
  phLabel: string
  phEmail: string
  phPhone: string
  phLocation: string
  phSummary: string
  phStart: string
  phEndYear: string
  phEduStart: string
  phEduEnd: string
  phLanguage: string
  phFluency: string
  highlightsLabel: string
  highlightsPh: string
  highlightsAdd: string
  skillsEmpty: string
  skillsLabel: string
  skillsPh: string
  skillsAdd: string
}

export const FR_PROFILE_EDITOR_LABELS: ProfileEditorLabels = {
  completeness: 'Complétude du profil',
  completenessTodo: 'À compléter : {list}',
  weak: {
    name: 'nom',
    title: 'titre',
    contact: 'coordonnées',
    summary: 'accroche',
    work: 'expériences détaillées',
    metrics: 'réalisations chiffrées',
    education: 'formation',
    skills: 'au moins cinq compétences',
  },
  sectionIdentity: 'Identité',
  sectionWork: 'Expériences',
  sectionSkills: 'Compétences',
  sectionLanguages: 'Langues',
  sectionEducation: 'Formation',
  sectionProjects: 'Projets (optionnel)',
  addWork: 'Ajouter une expérience',
  addLanguage: 'Ajouter une langue',
  addEducation: 'Ajouter une formation',
  addProject: 'Ajouter un projet',
  emptyWork: 'Aucune expérience. Ajoutez-en une.',
  emptyLanguages: 'Aucune langue.',
  emptyEducation: 'Aucune formation.',
  itemWork: 'Expérience',
  itemEducation: 'Formation',
  itemProject: 'Projet',
  moveUp: 'Monter',
  moveDown: 'Descendre',
  delete: 'Supprimer',
  fieldName: 'Nom complet',
  fieldLabel: 'Titre / Poste visé',
  fieldEmail: 'Email',
  fieldPhone: 'Téléphone',
  fieldLocation: 'Localisation',
  fieldSummary: 'Accroche / Résumé',
  fieldPosition: 'Poste',
  fieldCompany: 'Entreprise',
  fieldStart: 'Début',
  fieldEnd: 'Fin',
  fieldCurrent: 'Poste actuel',
  fieldToday: "Aujourd'hui (poste actuel)",
  fieldDegree: 'Diplôme',
  fieldArea: 'Domaine',
  fieldInstitution: 'Établissement',
  fieldProjectName: 'Nom',
  fieldDescription: 'Description',
  fieldLanguage: 'Langue',
  fieldFluency: 'Niveau',
  fieldLinks: 'Liens (portfolio, site, LinkedIn…)',
  fieldProjectUrl: 'Lien (facultatif)',
  addLink: 'Ajouter un lien',
  phLink: 'monportfolio.be',
  linkInvalid: "Cette adresse n'est pas un lien web : elle n'apparaîtra pas dans le CV.",
  phName: 'Prénom Nom',
  phLabel: 'Ex : Chef de projet digital',
  phEmail: 'vous@exemple.com',
  phPhone: '+33 6 …',
  phLocation: 'Paris, France',
  phSummary: '2-3 phrases résumant votre valeur ajoutée.',
  phStart: 'Jan 2020',
  phEndYear: '2022',
  phEduStart: '2017',
  phEduEnd: '2020',
  phLanguage: 'Anglais',
  phFluency: 'Courant / C1',
  highlightsLabel: 'Réalisations (une par ligne)',
  highlightsPh: 'Ex : Augmenté les ventes de 30 % en 6 mois grâce à…',
  highlightsAdd: 'Ajouter une réalisation',
  skillsEmpty: 'Aucune compétence, ajoutez-en ci-dessous',
  skillsLabel: 'Ajouter des compétences',
  skillsPh: 'Ex : React, Gestion de projet, Anglais courant',
  skillsAdd: 'Ajouter',
}

export const EN_PROFILE_EDITOR_LABELS: ProfileEditorLabels = {
  completeness: 'Profile completeness',
  completenessTodo: 'To complete: {list}',
  weak: {
    name: 'name',
    title: 'title',
    contact: 'contact details',
    summary: 'summary',
    work: 'detailed experience',
    metrics: 'quantified achievements',
    education: 'education',
    skills: 'at least five skills',
  },
  sectionIdentity: 'Identity',
  sectionWork: 'Experience',
  sectionSkills: 'Skills',
  sectionLanguages: 'Languages',
  sectionEducation: 'Education',
  sectionProjects: 'Projects (optional)',
  addWork: 'Add an experience',
  addLanguage: 'Add a language',
  addEducation: 'Add an education entry',
  addProject: 'Add a project',
  emptyWork: 'No experience yet. Add one.',
  emptyLanguages: 'No language yet.',
  emptyEducation: 'No education entry yet.',
  itemWork: 'Experience',
  itemEducation: 'Education',
  itemProject: 'Project',
  moveUp: 'Move up',
  moveDown: 'Move down',
  delete: 'Delete',
  fieldName: 'Full name',
  fieldLabel: 'Title / Target role',
  fieldEmail: 'Email',
  fieldPhone: 'Phone',
  fieldLocation: 'Location',
  fieldSummary: 'Summary',
  fieldPosition: 'Role',
  fieldCompany: 'Company',
  fieldStart: 'Start',
  fieldEnd: 'End',
  fieldCurrent: 'Current role',
  fieldToday: 'Present (current role)',
  fieldDegree: 'Degree',
  fieldArea: 'Field',
  fieldInstitution: 'Institution',
  fieldProjectName: 'Name',
  fieldDescription: 'Description',
  fieldLanguage: 'Language',
  fieldFluency: 'Level',
  fieldLinks: 'Links (portfolio, website, LinkedIn…)',
  fieldProjectUrl: 'Link (optional)',
  addLink: 'Add a link',
  phLink: 'myportfolio.com',
  linkInvalid: 'This is not a web address: it will not appear on the resume.',
  phName: 'First name Last name',
  phLabel: 'e.g. Digital project manager',
  phEmail: 'you@example.com',
  phPhone: '+44 7 …',
  phLocation: 'London, UK',
  phSummary: '2-3 sentences summarizing what you bring.',
  phStart: 'Jan 2020',
  phEndYear: '2022',
  phEduStart: '2017',
  phEduEnd: '2020',
  phLanguage: 'English',
  phFluency: 'Fluent / C1',
  highlightsLabel: 'Achievements (one per line)',
  highlightsPh: 'e.g. Grew sales by 30% in 6 months by…',
  highlightsAdd: 'Add an achievement',
  skillsEmpty: 'No skill yet, add some below',
  skillsLabel: 'Add skills',
  skillsPh: 'e.g. React, Project management, Fluent English',
  skillsAdd: 'Add',
}

/** Néerlandais (issue #137), traduction automatique pas encore relue. */
export const NL_PROFILE_EDITOR_LABELS: ProfileEditorLabels = {
  completeness: 'Volledigheid van het profiel',
  completenessTodo: 'Aan te vullen: {list}',
  weak: {
    name: 'naam',
    title: 'titel',
    contact: 'contactgegevens',
    summary: 'samenvatting',
    work: 'gedetailleerde ervaring',
    metrics: 'realisaties in cijfers',
    education: 'opleiding',
    skills: 'minstens vijf vaardigheden',
  },
  sectionIdentity: 'Identiteit',
  sectionWork: 'Ervaring',
  sectionSkills: 'Vaardigheden',
  sectionLanguages: 'Talen',
  sectionEducation: 'Opleiding',
  sectionProjects: 'Projecten (optioneel)',
  addWork: 'Een ervaring toevoegen',
  addLanguage: 'Een taal toevoegen',
  addEducation: 'Een opleiding toevoegen',
  addProject: 'Een project toevoegen',
  emptyWork: 'Nog geen ervaring. Voeg er een toe.',
  emptyLanguages: 'Nog geen taal.',
  emptyEducation: 'Nog geen opleiding.',
  itemWork: 'Ervaring',
  itemEducation: 'Opleiding',
  itemProject: 'Project',
  moveUp: 'Omhoog',
  moveDown: 'Omlaag',
  delete: 'Verwijderen',
  fieldName: 'Volledige naam',
  fieldLabel: 'Titel / Gewenste functie',
  fieldEmail: 'E-mail',
  fieldPhone: 'Telefoon',
  fieldLocation: 'Locatie',
  fieldSummary: 'Samenvatting',
  fieldPosition: 'Functie',
  fieldCompany: 'Bedrijf',
  fieldStart: 'Begin',
  fieldEnd: 'Einde',
  fieldCurrent: 'Huidige functie',
  fieldToday: 'Vandaag (huidige functie)',
  fieldDegree: 'Diploma',
  fieldArea: 'Domein',
  fieldInstitution: 'Instelling',
  fieldProjectName: 'Naam',
  fieldDescription: 'Beschrijving',
  fieldLanguage: 'Taal',
  fieldFluency: 'Niveau',
  fieldLinks: 'Links (portfolio, website, LinkedIn…)',
  fieldProjectUrl: 'Link (optioneel)',
  addLink: 'Een link toevoegen',
  phLink: 'mijnportfolio.be',
  linkInvalid: 'Dit is geen webadres: het verschijnt niet in het cv.',
  phName: 'Voornaam Naam',
  phLabel: 'Bv. Digitaal projectleider',
  phEmail: 'naam@voorbeeld.be',
  phPhone: '+32 4 …',
  phLocation: 'Gent, België',
  phSummary: '2 à 3 zinnen die samenvatten wat u meebrengt.',
  phStart: 'jan 2020',
  phEndYear: '2022',
  phEduStart: '2017',
  phEduEnd: '2020',
  phLanguage: 'Engels',
  phFluency: 'Vloeiend / C1',
  highlightsLabel: 'Realisaties (één per regel)',
  highlightsPh: 'Bv. Verkoop met 30 % verhoogd in 6 maanden dankzij…',
  highlightsAdd: 'Een realisatie toevoegen',
  skillsEmpty: 'Nog geen vaardigheden, voeg er hieronder toe',
  skillsLabel: 'Vaardigheden toevoegen',
  skillsPh: 'Bv. React, Projectbeheer, Vloeiend Engels',
  skillsAdd: 'Toevoegen',
}

/** Italien (issue #137), traduction automatique pas encore relue. */
export const IT_PROFILE_EDITOR_LABELS: ProfileEditorLabels = {
  completeness: 'Completezza del profilo',
  completenessTodo: 'Da completare: {list}',
  weak: {
    name: 'nome',
    title: 'titolo',
    contact: 'recapiti',
    summary: 'presentazione',
    work: 'esperienze dettagliate',
    metrics: 'risultati con numeri',
    education: 'formazione',
    skills: 'almeno cinque competenze',
  },
  sectionIdentity: 'Identità',
  sectionWork: 'Esperienze',
  sectionSkills: 'Competenze',
  sectionLanguages: 'Lingue',
  sectionEducation: 'Formazione',
  sectionProjects: 'Progetti (facoltativo)',
  addWork: 'Aggiungi un’esperienza',
  addLanguage: 'Aggiungi una lingua',
  addEducation: 'Aggiungi una formazione',
  addProject: 'Aggiungi un progetto',
  emptyWork: 'Nessuna esperienza. Aggiungine una.',
  emptyLanguages: 'Nessuna lingua.',
  emptyEducation: 'Nessuna formazione.',
  itemWork: 'Esperienza',
  itemEducation: 'Formazione',
  itemProject: 'Progetto',
  moveUp: 'Sposta su',
  moveDown: 'Sposta giù',
  delete: 'Elimina',
  fieldName: 'Nome completo',
  fieldLabel: 'Titolo / Ruolo cercato',
  fieldEmail: 'E-mail',
  fieldPhone: 'Telefono',
  fieldLocation: 'Località',
  fieldSummary: 'Presentazione',
  fieldPosition: 'Ruolo',
  fieldCompany: 'Azienda',
  fieldStart: 'Inizio',
  fieldEnd: 'Fine',
  fieldCurrent: 'Ruolo attuale',
  fieldToday: 'Oggi (ruolo attuale)',
  fieldDegree: 'Titolo di studio',
  fieldArea: 'Ambito',
  fieldInstitution: 'Istituto',
  fieldProjectName: 'Nome',
  fieldDescription: 'Descrizione',
  fieldLanguage: 'Lingua',
  fieldFluency: 'Livello',
  fieldLinks: 'Link (portfolio, sito, LinkedIn…)',
  fieldProjectUrl: 'Link (facoltativo)',
  addLink: 'Aggiungi un link',
  phLink: 'ilmioportfolio.it',
  linkInvalid: 'Questo indirizzo non è un link web: non comparirà nel CV.',
  phName: 'Nome Cognome',
  phLabel: 'Es.: Responsabile di progetto digitale',
  phEmail: 'tu@esempio.it',
  phPhone: '+39 3 …',
  phLocation: 'Milano, Italia',
  phSummary: '2-3 frasi che riassumono il valore che porti.',
  phStart: 'gen 2020',
  phEndYear: '2022',
  phEduStart: '2017',
  phEduEnd: '2020',
  phLanguage: 'Inglese',
  phFluency: 'Fluente / C1',
  highlightsLabel: 'Risultati (uno per riga)',
  highlightsPh: 'Es.: Aumentate le vendite del 30 % in 6 mesi grazie a…',
  highlightsAdd: 'Aggiungi un risultato',
  skillsEmpty: 'Nessuna competenza, aggiungine qui sotto',
  skillsLabel: 'Aggiungi competenze',
  skillsPh: 'Es.: React, Gestione di progetto, Inglese fluente',
  skillsAdd: 'Aggiungi',
}

/** Espagnol (issue #137), traduction automatique pas encore relue. */
export const ES_PROFILE_EDITOR_LABELS: ProfileEditorLabels = {
  completeness: 'Perfil completado',
  completenessTodo: 'Por completar: {list}',
  weak: {
    name: 'nombre',
    title: 'título',
    contact: 'datos de contacto',
    summary: 'presentación',
    work: 'experiencias detalladas',
    metrics: 'logros con cifras',
    education: 'formación',
    skills: 'al menos cinco competencias',
  },
  sectionIdentity: 'Identidad',
  sectionWork: 'Experiencia',
  sectionSkills: 'Competencias',
  sectionLanguages: 'Idiomas',
  sectionEducation: 'Formación',
  sectionProjects: 'Proyectos (opcional)',
  addWork: 'Añadir una experiencia',
  addLanguage: 'Añadir un idioma',
  addEducation: 'Añadir una formación',
  addProject: 'Añadir un proyecto',
  emptyWork: 'Ninguna experiencia. Añade una.',
  emptyLanguages: 'Ningún idioma.',
  emptyEducation: 'Ninguna formación.',
  itemWork: 'Experiencia',
  itemEducation: 'Formación',
  itemProject: 'Proyecto',
  moveUp: 'Subir',
  moveDown: 'Bajar',
  delete: 'Eliminar',
  fieldName: 'Nombre completo',
  fieldLabel: 'Título / Puesto buscado',
  fieldEmail: 'Correo',
  fieldPhone: 'Teléfono',
  fieldLocation: 'Ubicación',
  fieldSummary: 'Presentación',
  fieldPosition: 'Puesto',
  fieldCompany: 'Empresa',
  fieldStart: 'Inicio',
  fieldEnd: 'Fin',
  fieldCurrent: 'Puesto actual',
  fieldToday: 'Actualidad (puesto actual)',
  fieldDegree: 'Título',
  fieldArea: 'Área',
  fieldInstitution: 'Centro',
  fieldProjectName: 'Nombre',
  fieldDescription: 'Descripción',
  fieldLanguage: 'Idioma',
  fieldFluency: 'Nivel',
  fieldLinks: 'Enlaces (portfolio, web, LinkedIn…)',
  fieldProjectUrl: 'Enlace (opcional)',
  addLink: 'Añadir un enlace',
  phLink: 'miportfolio.es',
  linkInvalid: 'Esta dirección no es un enlace web: no aparecerá en el CV.',
  phName: 'Nombre Apellido',
  phLabel: 'P. ej.: Jefe de proyecto digital',
  phEmail: 'tu@ejemplo.es',
  phPhone: '+34 6 …',
  phLocation: 'Madrid, España',
  phSummary: '2 o 3 frases que resuman lo que aportas.',
  phStart: 'ene 2020',
  phEndYear: '2022',
  phEduStart: '2017',
  phEduEnd: '2020',
  phLanguage: 'Inglés',
  phFluency: 'Fluido / C1',
  highlightsLabel: 'Logros (uno por línea)',
  highlightsPh: 'P. ej.: Aumenté las ventas un 30 % en 6 meses gracias a…',
  highlightsAdd: 'Añadir un logro',
  skillsEmpty: 'Ninguna competencia, añade algunas abajo',
  skillsLabel: 'Añadir competencias',
  skillsPh: 'P. ej.: React, Gestión de proyectos, Inglés fluido',
  skillsAdd: 'Añadir',
}

/** Les libellés de l'éditeur par langue d'interface. */
export const PROFILE_EDITOR_LABELS: Record<UiLang, ProfileEditorLabels> = {
  fr: FR_PROFILE_EDITOR_LABELS,
  en: EN_PROFILE_EDITOR_LABELS,
  nl: NL_PROFILE_EDITOR_LABELS,
  it: IT_PROFILE_EDITOR_LABELS,
  es: ES_PROFILE_EDITOR_LABELS,
}
